import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    AlertTriangle,
    TrendingDown,
    DollarSign,
    Shield,
    Loader2,
    Bell,
    BellOff,
    Settings,
    Activity,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useTradingAccounts } from '@/api/trading-accounts/use-crm-trading-accounts';
import { useNavigate } from 'react-router-dom';
import { TraderPermissionGate } from '@/components/TraderPermissionGate';
import { ModulePermission, PermissionAction } from '@/types/permissions';

interface MarginCallWidgetProps {
    tradingAccountId?: string;
    className?: string;
    alertThresholds?: {
        marginCallThreshold?: number; // Percentage for margin call
        liquidationThreshold?: number; // Percentage for liquidation
        warningThreshold?: number; // Percentage for warning
    };
}

const MarginCallWidget: React.FC<MarginCallWidgetProps> = ({
    tradingAccountId,
    className,
    alertThresholds = {
        marginCallThreshold: 100, // 100% margin call level
        liquidationThreshold: 50, // 50% liquidation level
        warningThreshold: 150, // 150% warning level
    },
}) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [alertsEnabled, setAlertsEnabled] = useState(true);
    const [lastAlertTime, setLastAlertTime] = useState<Date | null>(null);

    const { data: accountsData, isLoading } = useTradingAccounts();

    const accounts = accountsData?.data || [];
    const selectedAccount = accounts.find((acc: any) => acc.id === tradingAccountId);

    // Calculate margin metrics
    const marginMetrics = {
        marginLevel: selectedAccount?.margin_level || 0,
        equity: selectedAccount?.equity || 0,
        balance: selectedAccount?.balance || 0,
        usedMargin: selectedAccount?.used_margin || 0,
        freeMargin: selectedAccount?.free_margin || 0,
        unrealizedPnl: selectedAccount?.unrealized_pnl || 0,
        isMarginCall: selectedAccount?.margin_status?.margin_call || false,
        isLiquidationRisk: selectedAccount?.margin_status?.liquidation_warning || false,
    };

    // Generate alerts
    const alerts: Array<{
        type: string;
        severity: string;
        title: string;
        message: string;
        icon: React.ReactNode;
    }> = [];

    if (alertsEnabled && selectedAccount) {
        // Liquidation risk alert
        if (
            marginMetrics.isLiquidationRisk ||
            marginMetrics.marginLevel <= alertThresholds.liquidationThreshold!
        ) {
            alerts.push({
                type: 'liquidation',
                severity: 'critical',
                title: 'Liquidation Risk',
                message: `Margin level is ${marginMetrics.marginLevel.toFixed(1)}%. Immediate action required to prevent liquidation.`,
                icon: <AlertTriangle className="h-4 w-4" />,
            });
        }

        // Margin call alert
        if (
            marginMetrics.isMarginCall ||
            marginMetrics.marginLevel <= alertThresholds.marginCallThreshold!
        ) {
            alerts.push({
                type: 'margin_call',
                severity: 'high',
                title: 'Margin Call',
                message: `Margin level is ${marginMetrics.marginLevel.toFixed(1)}%. Please deposit funds or close positions.`,
                icon: <TrendingDown className="h-4 w-4" />,
            });
        }

        // Warning alert
        if (
            marginMetrics.marginLevel <= alertThresholds.warningThreshold! &&
            marginMetrics.marginLevel > alertThresholds.marginCallThreshold!
        ) {
            alerts.push({
                type: 'warning',
                severity: 'medium',
                title: 'Margin Warning',
                message: `Margin level is ${marginMetrics.marginLevel.toFixed(1)}%. Consider reducing position sizes.`,
                icon: <Shield className="h-4 w-4" />,
            });
        }

        // Low free margin alert
        if (marginMetrics.freeMargin < 100 && marginMetrics.freeMargin > 0) {
            alerts.push({
                type: 'low_margin',
                severity: 'medium',
                title: 'Low Free Margin',
                message: `Free margin is ${marginMetrics.freeMargin.toFixed(2)}. Consider depositing funds.`,
                icon: <DollarSign className="h-4 w-4" />,
            });
        }
    }

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return 'border-red-500 bg-red-50 dark:bg-red-950/20';
            case 'high':
                return 'border-orange-500 bg-orange-50 dark:bg-orange-950/20';
            case 'medium':
                return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20';
            case 'low':
                return 'border-blue-500 bg-blue-50 dark:bg-blue-950/20';
            default:
                return 'border-gray-500 bg-gray-50 dark:bg-gray-950/20';
        }
    };

    const getSeverityTextColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return 'text-red-700 dark:text-red-300';
            case 'high':
                return 'text-orange-700 dark:text-orange-300';
            case 'medium':
                return 'text-yellow-700 dark:text-yellow-300';
            case 'low':
                return 'text-blue-700 dark:text-blue-300';
            default:
                return 'text-gray-700 dark:text-gray-300';
        }
    };

    const getMarginLevelColor = (level: number) => {
        if (level <= alertThresholds.liquidationThreshold!) return 'text-red-600';
        if (level <= alertThresholds.marginCallThreshold!) return 'text-orange-600';
        if (level <= alertThresholds.warningThreshold!) return 'text-yellow-600';
        return 'text-green-600';
    };

    const getMarginLevelStatus = (level: number) => {
        if (level <= alertThresholds.liquidationThreshold!) return 'Critical';
        if (level <= alertThresholds.marginCallThreshold!) return 'Margin Call';
        if (level <= alertThresholds.warningThreshold!) return 'Warning';
        return 'Safe';
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatNumber = (num: number, decimals: number = 2) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(num);
    };

    const handleViewPositions = () => {
        navigate('/trade');
    };

    const handleViewAccount = () => {
        navigate('/');
    };

    const handleToggleAlerts = () => {
        setAlertsEnabled(!alertsEnabled);
        enqueueSnackbar(`Margin alerts ${alertsEnabled ? 'disabled' : 'enabled'}`, {
            variant: alertsEnabled ? 'warning' : 'success',
        });
    };

    // Show alert notifications
    useEffect(() => {
        if (alertsEnabled && alerts.length > 0) {
            const now = new Date();
            const shouldShowAlert =
                !lastAlertTime || now.getTime() - lastAlertTime.getTime() > 30000; // 30 seconds

            if (shouldShowAlert) {
                const criticalAlerts = alerts.filter(alert => alert.severity === 'critical');
                if (criticalAlerts.length > 0) {
                    enqueueSnackbar(criticalAlerts[0].message, {
                        variant: 'error',
                        autoHideDuration: 10000,
                    });
                    setLastAlertTime(now);
                }
            }
        }
    }, [alerts, alertsEnabled, lastAlertTime, enqueueSnackbar]);

    if (!tradingAccountId) {
        return (
            <Card className={className}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Margin Monitoring
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center text-muted-foreground py-8">
                        <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Select a trading account to view margin status</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={className}>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Margin Monitoring
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleToggleAlerts}
                            className="h-8 w-8 p-0"
                        >
                            {alertsEnabled ? (
                                <Bell className="h-4 w-4" />
                            ) : (
                                <BellOff className="h-4 w-4" />
                            )}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleViewAccount}
                            className="h-8 w-8 p-0"
                        >
                            <Settings className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Alert Status */}
                        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                            <div className="flex items-center gap-2">
                                {alertsEnabled ? (
                                    <Bell className="h-4 w-4 text-green-500" />
                                ) : (
                                    <BellOff className="h-4 w-4 text-gray-500" />
                                )}
                                <span className="text-sm font-medium">
                                    Alerts {alertsEnabled ? 'Enabled' : 'Disabled'}
                                </span>
                            </div>
                            <Badge variant={alertsEnabled ? 'default' : 'secondary'}>
                                {alerts.length} Active
                            </Badge>
                        </div>

                        {/* Margin Level */}
                        <div className="text-center p-4 bg-muted rounded-lg">
                            <div
                                className={`text-3xl font-bold ${getMarginLevelColor(marginMetrics.marginLevel)}`}
                            >
                                {formatNumber(marginMetrics.marginLevel, 1)}%
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {getMarginLevelStatus(marginMetrics.marginLevel)}
                            </div>
                        </div>

                        {/* Account Metrics */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="text-center p-2 bg-muted rounded-lg">
                                <div className="text-sm font-bold">
                                    {formatCurrency(marginMetrics.equity)}
                                </div>
                                <div className="text-xs text-muted-foreground">Equity</div>
                            </div>
                            <div className="text-center p-2 bg-muted rounded-lg">
                                <div className="text-sm font-bold">
                                    {formatCurrency(marginMetrics.freeMargin)}
                                </div>
                                <div className="text-xs text-muted-foreground">Free Margin</div>
                            </div>
                        </div>

                        {/* Alerts List */}
                        {alerts.length === 0 ? (
                            <div className="text-center text-muted-foreground py-6">
                                <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No margin alerts</p>
                                <p className="text-xs">Account is within safe margin levels</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {alerts.map((alert, index) => (
                                    <Alert
                                        key={index}
                                        className={`${getSeverityColor(alert.severity)} border`}
                                    >
                                        <div className={`${getSeverityTextColor(alert.severity)}`}>
                                            {alert.icon}
                                        </div>
                                        <AlertDescription
                                            className={`${getSeverityTextColor(alert.severity)}`}
                                        >
                                            <div className="font-medium">{alert.title}</div>
                                            <div className="text-sm mt-1">{alert.message}</div>
                                        </AlertDescription>
                                    </Alert>
                                ))}
                            </div>
                        )}

                        {/* Quick Actions */}
                        {alerts.length > 0 && (
                            <div className="pt-3 border-t">
                                <div className="grid grid-cols-2 gap-2">
                                    <TraderPermissionGate
                                        module={ModulePermission.TRADING}
                                        action={PermissionAction.VIEW}
                                    >
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleViewPositions}
                                        >
                                            <Activity className="h-4 w-4 mr-2" />
                                            Positions
                                        </Button>
                                    </TraderPermissionGate>
                                    <TraderPermissionGate
                                        module={ModulePermission.TRADING_ACCOUNTS}
                                        action={PermissionAction.VIEW}
                                    >
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleViewAccount}
                                        >
                                            <DollarSign className="h-4 w-4 mr-2" />
                                            Deposit
                                        </Button>
                                    </TraderPermissionGate>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default MarginCallWidget;
